import { z } from "zod";

/**
 * A repository's checked-in description of how to warm, run, and tear down a
 * Rivets workspace — `.rivets/settings.toml` at the worktree root.
 *
 * Why a repo file at all: a Rivets worker clones into `~/.rivets/repos/<id>`
 * and cuts every worktree from that clone, so a task always starts on a tree
 * that has never had `node_modules`, a build output, or a `.env` in it. Only
 * the repository knows how to fix that, so the repository says so, once, for
 * every operator.
 *
 * TOML rather than JSON because these values are shell: a setup script is
 * routinely a dozen lines, and TOML's `"""` block keeps it readable where JSON
 * would force it onto one escaped line. Parsing happens on the worker (which
 * has `Bun.TOML`); everything after the parse is validated here so the clients
 * and the API share one definition of what a script is.
 */

/** Worktree-root-relative location of the checked-in settings file. */
export const PROJECT_SETTINGS_PATH = ".rivets/settings.toml";
/** A settings file larger than this is a mistake or an attack, not config. */
export const PROJECT_SETTINGS_MAX_BYTES = 64 * 1024;
export const PROJECT_SCRIPT_MAX_RUN_SCRIPTS = 24;
/** Default ceiling for a setup or archive script. Long enough for a cold
 * `bun install` on a large monorepo, short enough that a script waiting on
 * stdin cannot pin a worktree open forever. */
export const PROJECT_SCRIPT_DEFAULT_TIMEOUT_SECONDS = 900;
export const PROJECT_SCRIPT_MAX_TIMEOUT_SECONDS = 3_600;

/**
 * Authorization epoch every script terminal is created with.
 *
 * A client's terminal gets its epoch from the API, which issues it when the
 * client asks for the terminal. A script terminal has no such exchange — the
 * worker creates it and the API registers what it is told — so both sides need
 * to agree on one constant instead. Re-running a script bumps the terminal's
 * incarnation, never its epoch: the epoch says who may address the terminal,
 * and that does not change between runs.
 */
export const SCRIPT_TERMINAL_AUTH_EPOCH = 1;

/** Script keys are used in ids, menus, and log lines, so they stay boring. */
export const PROJECT_SCRIPT_ID = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,47}$/u);

/** Ids the two lifecycle hooks own. A `[scripts.run.setup]` entry would
 * otherwise parse cleanly and then be shadowed by the lifecycle script of the
 * same name, running a command the repository never asked for. */
export const RESERVED_SCRIPT_IDS = ["setup", "archive"] as const;

/**
 * A closed set, not Lucide's open one: every client has to draw these, and the
 * native apps map them to SF Symbols. An unknown name degrades to `play`
 * rather than failing the file, matching Conductor.
 */
export const ProjectScriptIcon = z.enum([
  "play", "hammer", "test-tube", "circle-check", "monitor",
  "database", "terminal", "rocket", "book", "wrench", "globe", "package",
]);
export type ProjectScriptIcon = z.infer<typeof ProjectScriptIcon>;

const Command = z.string().min(1).max(16_384);
/** Relative, no `..`, no absolute paths: a script's cwd stays in its worktree. */
const RelativeCwd = z.string().max(512).refine(
  (value) => value.length > 0 && !value.startsWith("/") && !value.startsWith("\\")
    && !/(^|[\\/])\.\.($|[\\/])/u.test(value) && !/^[A-Za-z]:/u.test(value),
  "script cwd must be a relative path inside the worktree",
);

/** `setup` and `archive`: one per repository, run by the worker, not a user. */
export const ProjectLifecycleScript = z.object({
  command: Command,
  cwd: RelativeCwd.optional(),
  /**
   * Whether the agent starts before this script finishes.
   *
   * False by default, which is the opposite of T3 Code's `runOnWorktreeCreate`
   * default and deliberate: their human is watching a setup card and can retry,
   * whereas a Rivets job runs unattended, so an agent whose first tool call
   * lands on a half-installed tree just fails the task. Only ignored for
   * `archive`, which nothing waits on.
   */
  async: z.boolean().default(false),
  timeoutSeconds: z.number().int().min(1).max(PROJECT_SCRIPT_MAX_TIMEOUT_SECONDS)
    .default(PROJECT_SCRIPT_DEFAULT_TIMEOUT_SECONDS),
});
export type ProjectLifecycleScript = z.infer<typeof ProjectLifecycleScript>;

/** A `run` script: user-triggered, named, and shown in the Run menu. */
export const ProjectRunScript = z.object({
  id: PROJECT_SCRIPT_ID,
  name: z.string().min(1).max(80),
  command: Command,
  cwd: RelativeCwd.optional(),
  icon: ProjectScriptIcon.default("play"),
  /** Pre-selected in the Run split button. First wins if several claim it. */
  isDefault: z.boolean().default(false),
  /** Kept out of the menu but still runnable by id. */
  hidden: z.boolean().default(false),
});
export type ProjectRunScript = z.infer<typeof ProjectRunScript>;

export const ProjectRunMode = z.enum(["concurrent", "nonconcurrent"]);
export type ProjectRunMode = z.infer<typeof ProjectRunMode>;

export const ProjectScripts = z.object({
  setup: ProjectLifecycleScript.optional(),
  archive: ProjectLifecycleScript.optional(),
  /** `nonconcurrent` stops the other run scripts before starting one. Matches
   * Conductor, and is the right default for dev servers that share a port. */
  runMode: ProjectRunMode.default("nonconcurrent"),
  run: z.array(ProjectRunScript).max(PROJECT_SCRIPT_MAX_RUN_SCRIPTS).default([]),
});
export type ProjectScripts = z.infer<typeof ProjectScripts>;

export const EMPTY_PROJECT_SCRIPTS: ProjectScripts = { runMode: "nonconcurrent", run: [] };

// ---------------------------------------------------------------------------
// TOML shape → canonical shape
// ---------------------------------------------------------------------------

/**
 * TOML is written in snake_case and allows a bare string where a table is also
 * valid (`setup = "bun install"`). Normalising here rather than in the zod
 * schema keeps the canonical type clean for every consumer that never sees the
 * file — the API, the clients, and the worker's own runner.
 */
function normalizeLifecycle(raw: unknown): unknown {
  if (typeof raw === "string") return { command: raw };
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const table = raw as Record<string, unknown>;
  return {
    ...(table.command === undefined ? {} : { command: table.command }),
    ...(table.cwd === undefined ? {} : { cwd: table.cwd }),
    ...(table.async === undefined ? {} : { async: table.async }),
    ...(table.timeout_seconds === undefined
      ? (table.timeoutSeconds === undefined ? {} : { timeoutSeconds: table.timeoutSeconds })
      : { timeoutSeconds: table.timeout_seconds }),
  };
}

function normalizeRunScripts(raw: unknown): unknown[] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>).map(([id, value]) => {
    const table = (value && typeof value === "object" && !Array.isArray(value)
      ? value
      : {}) as Record<string, unknown>;
    const options = (table.options && typeof table.options === "object" && !Array.isArray(table.options)
      ? table.options
      : {}) as Record<string, unknown>;
    // An unrecognised icon becomes `play` rather than rejecting the file: one
    // typo in a shared repo file must not cost everyone their Run menu.
    const icon = ProjectScriptIcon.safeParse(table.icon);
    return {
      id,
      name: typeof table.name === "string" && table.name.trim().length > 0 ? table.name.trim() : id,
      ...(table.command === undefined ? {} : { command: table.command }),
      ...(table.cwd === undefined ? (options.cwd === undefined ? {} : { cwd: options.cwd }) : { cwd: table.cwd }),
      icon: icon.success ? icon.data : "play",
      isDefault: table.default === true,
      hidden: table.hide === true,
    };
  });
}

export interface ProjectSettingsParseResult {
  scripts: ProjectScripts;
  /** Human-readable, already bounded. Present when part of the file was
   * dropped; the rest of the file still applies. */
  problems: string[];
}

/**
 * Validate an already-parsed TOML document.
 *
 * Never throws. A repository file is untrusted input from everyone with commit
 * access, so a malformed one degrades to "this repo has no scripts" with a
 * reason the clients can show, rather than failing a task.
 */
export function parseProjectSettings(raw: unknown): ProjectSettingsParseResult {
  const problems: string[] = [];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { scripts: EMPTY_PROJECT_SCRIPTS, problems: ["settings file is not a TOML table"] };
  }
  const scriptsTable = (raw as Record<string, unknown>).scripts;
  if (scriptsTable === undefined) return { scripts: EMPTY_PROJECT_SCRIPTS, problems };
  if (!scriptsTable || typeof scriptsTable !== "object" || Array.isArray(scriptsTable)) {
    return { scripts: EMPTY_PROJECT_SCRIPTS, problems: ["[scripts] is not a table"] };
  }
  const table = scriptsTable as Record<string, unknown>;

  const lifecycle = (key: "setup" | "archive"): ProjectLifecycleScript | undefined => {
    if (table[key] === undefined) return undefined;
    const parsed = ProjectLifecycleScript.safeParse(normalizeLifecycle(table[key]));
    if (parsed.success) return parsed.data;
    problems.push(`[scripts].${key} was ignored: ${firstIssue(parsed.error)}`);
    return undefined;
  };

  // Evaluated once: calling `lifecycle` twice would report the same problem twice.
  const setup = lifecycle("setup");
  const archive = lifecycle("archive");

  const runMode = ProjectRunMode.safeParse(table.run_mode ?? table.runMode);
  if (table.run_mode !== undefined && !runMode.success) {
    problems.push('[scripts].run_mode must be "concurrent" or "nonconcurrent"');
  }

  const run: ProjectRunScript[] = [];
  for (const candidate of normalizeRunScripts(table.run)) {
    if (run.length >= PROJECT_SCRIPT_MAX_RUN_SCRIPTS) {
      problems.push(`only the first ${PROJECT_SCRIPT_MAX_RUN_SCRIPTS} run scripts are used`);
      break;
    }
    const id = (candidate as { id?: unknown }).id;
    if (typeof id === "string" && (RESERVED_SCRIPT_IDS as readonly string[]).includes(id)) {
      problems.push(`[scripts.run.${id}] was ignored: "${id}" is reserved for the lifecycle script of that name`);
      continue;
    }
    const parsed = ProjectRunScript.safeParse(candidate);
    if (parsed.success) {
      run.push(parsed.data);
      continue;
    }
    problems.push(`[scripts.run.${typeof id === "string" ? id : "?"}] was ignored: ${firstIssue(parsed.error)}`);
  }
  // A second `default = true` is a merge accident, not an instruction.
  let claimed = false;
  const deduped = run.map((script) => {
    if (!script.isDefault) return script;
    if (claimed) return { ...script, isDefault: false };
    claimed = true;
    return script;
  });

  return {
    scripts: {
      ...(setup ? { setup } : {}),
      ...(archive ? { archive } : {}),
      runMode: runMode.success ? runMode.data : "nonconcurrent",
      run: deduped,
    },
    problems: problems.slice(0, 20),
  };
}

function firstIssue(error: z.ZodError): string {
  const issue = error.issues[0];
  if (!issue) return "invalid";
  const path = issue.path.join(".");
  return path ? `${path}: ${issue.message}` : issue.message;
}

/** The run script the Run button triggers when nobody has picked one. */
export function defaultRunScript(scripts: ProjectScripts): ProjectRunScript | undefined {
  return scripts.run.find((script) => script.isDefault && !script.hidden)
    ?? scripts.run.find((script) => !script.hidden);
}

/** The scripts a Run menu should list, in file order. */
export function visibleRunScripts(scripts: ProjectScripts): ProjectRunScript[] {
  return scripts.run.filter((script) => !script.hidden);
}
